import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Icon } from '@codex/icons';
import { resolveByCountry } from '@codex/helpers';
import { Button, Input } from '@codex/basics';
import {
  generatePriceFilterCheckboxesItems,
  touchDeviceType,
  getPriceRangeFromFilterCheckboxesItems
} from './utils/filterHelpers';

import CheckboxList from './presentionalComponents/CheckboxList';
import FiltersMobileButtons from './presentionalComponents/FiltersMobileButtons';
import { changePriceFilter } from '../../reduck/listingPagePages.reduck';
import { getActiveListingPage } from '../../reduck/selectors';

import {
  OptionList,
  PriceRange,
  PriceRangeButton,
  PriceRangeCurrency,
  PriceRangeFirstInput,
  PriceRangeInputs,
  PriceRangeSecondInput
} from './styled';

class FilterPriceRange extends PureComponent {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      (typeof prevState.priceFilterCheckboxesItems === 'undefined' &&
        nextProps.priceFilterRanges) || // Condition for first render
      prevState.previousMinPrice !== nextProps.currentActiveMinPrice || // if minPrice has changed, checkboxes items has to be regenerated from props
      prevState.previousMaxPrice !== nextProps.currentActiveMaxPrice || // if maxPrice has changed, checkboxes items has to be regenerated from props
      (nextProps.productsRequestErrorMessage !== null &&
        prevState.previousProductsPostXhrError === null) // if error occurs during loading data, checkboxes items has to set from filterItemData prop
    ) {
      return {
        previousMinPrice: nextProps.currentActiveMinPrice,
        previousMaxPrice: nextProps.currentActiveMaxPrice,
        priceFromInputValue: nextProps.currentActiveMinPrice,
        priceToInputValue: nextProps.currentActiveMaxPrice,
        previousProductsPostXhrError: nextProps.productsRequestErrorMessage,

        priceFilterCheckboxesItems: nextProps.priceFilterRanges
          ? generatePriceFilterCheckboxesItems(
            nextProps.priceFilterRanges,
            nextProps.currentActiveMinPrice,
            nextProps.currentActiveMaxPrice
          )
          : undefined
      };
    }

    return {
      previousProductsPostXhrError: nextProps.productsRequestErrorMessage
    };
  }

  constructor(props) {
    super(props);

    this.state = {};
  }

  onCheckboxOptionChange(filterId, checkboxData) {
    const { isTouchDeviceType } = this.props;

    let newFilterCheckboxesItems;
    if (this.state.priceFilterCheckboxesItems) {
      newFilterCheckboxesItems = this.state.priceFilterCheckboxesItems.map((checkboxItem) =>
        (checkboxItem.value.lowerRangeInclusive === checkboxData.value.lowerRangeInclusive
          ? {
            ...checkboxItem,
            selected: !checkboxItem.selected
          }
          : checkboxItem)
      );
    }

    const { currentMinPrice, currentMaxPrice } = getPriceRangeFromFilterCheckboxesItems(
      newFilterCheckboxesItems
    );

    this.setState(
      {
        priceFilterCheckboxesItems: newFilterCheckboxesItems,
        priceFromInputValue: currentMinPrice,
        priceToInputValue: currentMaxPrice
      },
      () => {
        if (!isTouchDeviceType) {
          // for desktop device fitlers are updated immediately after checkbox click
          this.setUserSetPriceFilter();
        }
      }
    );
  }

  onMobileResetButtonClick() {
    const { currentActiveMinPrice, currentActiveMaxPrice, changePriceFilterValue } = this.props;

    if (
      typeof currentActiveMinPrice === 'undefined' &&
      typeof currentActiveMaxPrice === 'undefined'
    ) {
      // Because currently on the listing page there is no price filter active,
      // it just clears checkboxes and price inputs
      this.setState((prevState) => ({
        priceFilterCheckboxesItems: prevState.priceFilterCheckboxesItems
          ? prevState.priceFilterCheckboxesItems.map((checkboxItem) => ({
            ...checkboxItem,
            selected: false
          }))
          : prevState.priceFilterCheckboxesItems,
        priceFromInputValue: undefined,
        priceToInputValue: undefined
      }));
    } else {
      // A price filter is active on the listing page,
      // so we need to get new data for listing page without any price filter active
      changePriceFilterValue();
    }
  }

  setUserSetPriceFilter() {
    const { currentActiveMinPrice, currentActiveMaxPrice, changePriceFilterValue } = this.props;

    // if value of any input is 0, it's changed to 'undefined' value in order to solve the following edge case:
    // this.state.priceFromInputValue === 0 && this.state.priceToInputValue === 0
    // In this case when both inputs values are 0, it has to behave like no price filter range is selected
    const parsedPriceFromInputValue =
      this.state.priceFromInputValue !== 0 ? this.state.priceFromInputValue : undefined;
    const parsedPriceToInputValue =
      this.state.priceToInputValue !== 0 ? this.state.priceToInputValue : undefined;

    if (
      currentActiveMinPrice !== parsedPriceFromInputValue ||
      currentActiveMaxPrice !== parsedPriceToInputValue
    ) {
      // min price has to be always defined in changePriceFilterValue
      if (
        typeof parsedPriceFromInputValue === 'undefined' &&
        typeof parsedPriceToInputValue !== 'undefined'
      ) {
        changePriceFilterValue(0, parsedPriceToInputValue);
      } else {
        changePriceFilterValue(parsedPriceFromInputValue, parsedPriceToInputValue);
      }
    }
  }

  resetCheckboxOptions() {
    this.setState((prevState) => ({
      priceFilterCheckboxesItems: prevState.priceFilterCheckboxesItems
        ? prevState.priceFilterCheckboxesItems.map((checkboxItem) => ({
          ...checkboxItem,
          selected: false
        }))
        : prevState.priceFilterCheckboxesItems
    }));
  }

  isPriceInputsValid() {
    const priceFromInputValue = this.state.priceFromInputValue;
    const priceToInputValue = this.state.priceToInputValue;

    // priceFromInputValue can't be equals or greater than priceToInputValue
    if (
      typeof priceFromInputValue !== 'undefined' &&
      typeof priceToInputValue !== 'undefined' &&
      priceFromInputValue > priceToInputValue
    ) {
      return false;
    }

    return true;
  }

  renderPriceInputs() {
    const { loading, isTouchDeviceType } = this.props;

    return (
      <PriceRange className={'dc-filter__price-range'}>
        <PriceRangeCurrency className={'dc-filter__price-range__currency'}>
          {resolveByCountry('£', '€')}
        </PriceRangeCurrency>
        <PriceRangeInputs className={'dc-filter__price-range__inputs'}>
          <PriceRangeFirstInput className={'dc-filter__price-range__inputs__first-input'}>
            <Input
              name={'priceFromInput'}
              type={'text'}
              interactivePlaceholder
              placeholderText={!loading ? 'From' : ''} // add to translations
              value={
                typeof this.state.priceFromInputValue === 'number'
                  ? this.state.priceFromInputValue
                  : ''
              }
              onChange={(e) => {
                this.resetCheckboxOptions();
                this.setState({
                  priceFromInputValue:
                    e.target.value !== '' &&
                    typeof Number(e.target.value) === 'number' &&
                    !Number.isNaN(Number(e.target.value))
                      ? Number(e.target.value)
                      : undefined
                });
              }}
              valid={this.isPriceInputsValid()}
              attrs={{
                container: { style: { marginRight: '5px' } },
                input: { style: { padding: '10px 15px' } }
              }}
            />
          </PriceRangeFirstInput>
          <PriceRangeSecondInput className={'dc-filter__price-range__inputs__second-input'}>
            <Input
              name={'priceToInput'}
              type={'text'}
              interactivePlaceholder
              placeholderText={!loading ? 'To' : ''} // add to translations
              value={
                typeof this.state.priceToInputValue === 'number' ? this.state.priceToInputValue : ''
              }
              onChange={(e) => {
                this.resetCheckboxOptions();
                this.setState({
                  priceToInputValue:
                    e.target.value !== '' &&
                    typeof Number(e.target.value) === 'number' &&
                    !Number.isNaN(Number(e.target.value))
                      ? Number(e.target.value)
                      : undefined
                });
              }}
              valid={this.isPriceInputsValid()}
              attrs={{
                input: { style: { padding: '10px 15px' } }
              }}
            />
          </PriceRangeSecondInput>
        </PriceRangeInputs>
        {!isTouchDeviceType && (
          <PriceRangeButton className={'dc-filter__price-range__button'}>
            <Button
              small
              onClick={() => {
                this.setUserSetPriceFilter();
              }}
              disabled={
                (typeof this.state.priceFromInputValue === 'undefined' &&
                  typeof this.state.priceToInputValue === 'undefined') ||
                !this.isPriceInputsValid()
              }
            >
              Refresh
              {' '}
              {/* TODO: add to translations */}
            </Button>
          </PriceRangeButton>
        )}
      </PriceRange>
    );
  }

  renderCheckboxList() {
    const { isTouchDeviceType, priceFilterRanges } = this.props;

    return priceFilterRanges ? (
      <OptionList className={'dc-filter__option-list'}>
        <CheckboxList
          id={'price'}
          checkboxSize={isTouchDeviceType ? 'small' : 'extra-small'}
          toggleCheckboxOption={({ filterId, checkboxData }) => {
            this.onCheckboxOptionChange(filterId, checkboxData);
          }}
          priceFilterRanges={priceFilterRanges}
          priceFilterCheckboxesItems={this.state.priceFilterCheckboxesItems}
        />
      </OptionList>
    ) : null;
  }

  render() {
    const {
      loading,
      loadingPriceFilter,
      isTouchDeviceType,
      hideFilterItem,
      productsRequestErrorMessage,
      noProductsOnPage
    } = this.props;

    return (
      <>
        {loading &&
          loadingPriceFilter &&
          !isTouchDeviceType /* Icon is just shown for desktop during loading */ && (
            <Icon
              icon={'Spinner'}
              style={{
                position: 'absolute',
                zIndex: 4,
                marginLeft: 'calc(50% - 20px)',
                width: '40px',
                height: '40px'
              }}
              spin
            />
        )}
        {this.renderCheckboxList()}
        {this.renderPriceInputs()}
        {isTouchDeviceType && (
          <FiltersMobileButtons
            resetButtonDisabled={
              typeof this.state.priceFromInputValue === 'undefined' &&
              typeof this.state.priceToInputValue === 'undefined'
            }
            applyButtonDisabled={
              (typeof this.state.priceFromInputValue === 'undefined' &&
                typeof this.state.priceToInputValue === 'undefined') ||
              !this.isPriceInputsValid()
            }
            resetButtonLabel={'Clear'} // TODO: add to translations
            applyButtonLabel={'Apply'} // TODO: add to translations
            onResetButtonClick={() => {
              this.onMobileResetButtonClick();
            }}
            onApplyButtonClick={() => {
              hideFilterItem();
              this.setUserSetPriceFilter();
            }}
            showErrorMessage={!!productsRequestErrorMessage}
            showWarningMessage={noProductsOnPage}
          />
        )}
      </>
    );
  }
}

FilterPriceRange.displayName = 'FilterPriceRange';

FilterPriceRange.propTypes = {
  loading: PropTypes.bool.isRequired,
  loadingPriceFilter: PropTypes.bool,
  productsRequestErrorMessage: PropTypes.string,
  noProductsOnPage: PropTypes.bool,
  isTouchDeviceType: PropTypes.bool.isRequired,
  currentActiveMinPrice: PropTypes.number,
  currentActiveMaxPrice: PropTypes.number,
  priceFilterRanges: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      lowerRangeInclusive: PropTypes.number.isRequired,
      upperRangeInclusive: PropTypes.number
    })
  ),
  changePriceFilterValue: PropTypes.func,
  hideFilterItem: PropTypes.func
};

FilterPriceRange.defaultProps = {
  loadingPriceFilter: false,
  productsRequestErrorMessage: null,
  noProductsOnPage: false,
  currentActiveMinPrice: undefined,
  currentActiveMaxPrice: undefined,
  priceFilterRanges: undefined,
  changePriceFilterValue: () => {},
  hideFilterItem: () => {}
};

const mapStateToProps = (state) => ({
  productsRequestErrorMessage: state.listingPage.commonData.xhrStatus.productsRequestErrorMessage,
  noProductsOnPage: getActiveListingPage(state).productList.length === 0,
  isTouchDeviceType: state.listingPage.browser
    ? touchDeviceType(state.listingPage.browser.device.deviceType)
    : false,
  priceFilterRanges:
    !getActiveListingPage(state).searchListingPage &&
    state.listingPage.staticData.priceFilterRangesData
      ? state.listingPage.staticData.priceFilterRangesData
      : undefined,
  currentActiveMinPrice:
    typeof getActiveListingPage(state).pageAttributes.price_min !== 'undefined'
      ? Number(getActiveListingPage(state).pageAttributes.price_min)
      : undefined,
  currentActiveMaxPrice:
    typeof getActiveListingPage(state).pageAttributes.price_max !== 'undefined'
      ? Number(getActiveListingPage(state).pageAttributes.price_max)
      : undefined
});

const mapDispatchToProps = (dispatch) => ({
  changePriceFilterValue: (priceMin, priceMax) => {
    dispatch(changePriceFilter(priceMin, priceMax));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterPriceRange);
